import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { type Component, createEffect, createResource, createSignal, For, onCleanup, Show } from 'solid-js'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import GoBack from '~/components/GoBack'
import Skeleton from '~/components/Skeleton'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { usePermissions } from '~/hooks/usePermissions'
import { type OrderType, orderService } from '~/services/order.service'
import { setTrailOverride } from '~/stores/breadcrumb'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'
import { formatDateTime, formatMoney } from '~/utils/settings'
import { orderStatusColors, orderStatusLabels, paymentStatusColors, paymentStatusLabels } from '~/utils/status'

/**
 * Detalle de un pedido: qué se compró, a qué precio y qué pasó con el cobro.
 *
 * Es de sólo lectura salvo por tres acciones, porque un pedido no se edita: lo
 * que se vendió, se vendió. Se puede generar un enlace de pago, preguntarle a la
 * pasarela cómo quedó, y cancelarlo si nadie lo ha pagado todavía.
 */
const PedidoDetallePage: Component = () => {
  const params = useParams()
  const toast = useToast()
  const alert = useAlert()
  const { can } = usePermissions()
  const { settings } = useCompanySettings()
  const [busy, setBusy] = createSignal(false)

  const [order, { refetch }] = createResource(
    () => Number(params.id),
    async (id: number) => {
      try {
        return await orderService.get(id)
      } catch {
        return null
      }
    }
  )

  // La miga de pan no puede leer el recurso dentro de <Title>: se calcula aquí.
  createEffect(() => {
    const number = order()?.number
    setTrailOverride(['Pedidos', number || 'Detalle'])
  })
  onCleanup(() => setTrailOverride(null))

  const money = (value: unknown, currency: string) => formatMoney(value as number | string, currency, settings()?.currencies ?? [])

  const generarEnlace = async (o: OrderType) => {
    setBusy(true)
    try {
      const { checkoutUrl } = await orderService.pay(o.id)
      await navigator.clipboard.writeText(checkoutUrl).catch(() => {})
      toast.addToast({
        title: 'Enlace de pago generado',
        description: 'Copiado al portapapeles',
        type: 'success',
        action: { label: 'Abrir', onClick: () => window.open(checkoutUrl, '_blank', 'noopener') }
      })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const sincronizar = async (o: OrderType) => {
    setBusy(true)
    try {
      await orderService.sync(o.id)
      toast.addToast({ title: 'Estado actualizado con la pasarela', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const cancelar = async (o: OrderType) => {
    const ok = await alert.confirm({
      title: 'Cancelar pedido',
      message: `El pedido ${o.number} quedará cancelado.`,
      confirmText: 'Sí, cancelar'
    })
    if (!ok) return
    setBusy(true)
    try {
      await orderService.cancel(o.id)
      toast.addToast({ title: 'Pedido cancelado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Container>
      <Title>Detalle del pedido | Florez Jaramillo</Title>

      <Show
        when={!order.loading}
        fallback={
          <Card>
            <Card.Body>
              <div class="space-y-3">
                <Skeleton height="2rem" width="40%" />
                <Skeleton height="1rem" width="70%" />
                <Skeleton height="12rem" />
              </div>
            </Card.Body>
          </Card>
        }
      >
        <Show
          when={order()}
          fallback={
            <Card>
              <Card.Body>
                <div class="text-center py-10 space-y-4">
                  <p class="text-foreground-subtle">No pudimos cargar este pedido. Puede que ya no exista.</p>
                  <GoBack label="Volver a pedidos" fallbackPath="/admin/pedidos" />
                </div>
              </Card.Body>
            </Card>
          }
        >
          {data => (
            <div class="space-y-4">
              {/* Cabecera con estado y acciones */}
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div class="flex items-center gap-2">
                    <h1 class="text-2xl font-bold text-foreground">{data().number}</h1>
                    <Chip color={orderStatusColors[data().status]} variant="soft" size="sm">
                      {orderStatusLabels[data().status]}
                    </Chip>
                  </div>
                  <p class="text-sm text-foreground-subtle">
                    Creado el {formatDateTime(data().createdAt, settings()?.locale)}
                    <Show when={data().paidAt}>
                      {' · '}Pagado el {formatDateTime(data().paidAt, settings()?.locale)}
                    </Show>
                  </p>
                </div>

                <div class="flex flex-wrap gap-2">
                  <Show when={can(P.orders.update) && data().status === 'pending'}>
                    <Button disabled={busy()} onClick={() => generarEnlace(data())} prependIcon="material-symbols-light:link">
                      Generar enlace de pago
                    </Button>
                  </Show>
                  <Show when={can(P.payments.read) && data().payments?.length}>
                    <Button variant="outlined" color="default" disabled={busy()} onClick={() => sincronizar(data())} prependIcon="material-symbols-light:sync">
                      Consultar pasarela
                    </Button>
                  </Show>
                  <Show when={can(P.orders.update) && data().status === 'pending'}>
                    <Button variant="soft" color="danger" disabled={busy()} onClick={() => cancelar(data())}>
                      Cancelar
                    </Button>
                  </Show>
                </div>
              </div>

              {/*
                Cobrado y sin descontar del inventario. Es el caso que no se
                arregla solo: el dinero entró, así que hay que reponer, despachar
                parcial o devolver.
              */}
              <Show when={data().status === 'paid' && !data().stockAppliedAt}>
                <Alert variant="danger">
                  Este pedido se cobró pero <strong>no se descontó del inventario</strong>, casi siempre por falta de existencias. Hay que reponer y ajustar el stock, despachar parcialmente o
                  reembolsar al cliente.
                </Alert>
              </Show>

              <div class="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
                <div class="xl:col-span-2 space-y-4">
                  {/* Líneas */}
                  <Card>
                    <Card.Header>
                      <h2 class="text-lg font-semibold text-foreground">Lo que se compró</h2>
                    </Card.Header>
                    <Card.Body>
                      <div class="overflow-x-auto">
                        <table class="w-full text-sm">
                          <thead>
                            <tr class="text-left text-foreground-subtle border-b border-border">
                              <th class="py-2 pr-3 font-medium">Descripción</th>
                              <th class="py-2 px-3 font-medium text-right">Cant.</th>
                              <th class="py-2 px-3 font-medium text-right">Precio</th>
                              <th class="py-2 pl-3 font-medium text-right">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={data().items ?? []}>
                              {item => (
                                <tr class="border-b border-border/50">
                                  <td class="py-2 pr-3">
                                    <div class="text-foreground">{item.name}</div>
                                    <Show when={item.sku}>
                                      <div class="text-xs text-foreground-subtle">SKU {item.sku}</div>
                                    </Show>
                                  </td>
                                  <td class="py-2 px-3 text-right">{Number(item.quantity)}</td>
                                  <td class="py-2 px-3 text-right">{money(item.unitPrice, data().currency)}</td>
                                  <td class="py-2 pl-3 text-right font-medium">{money(item.subtotal, data().currency)}</td>
                                </tr>
                              )}
                            </For>
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Intentos de cobro */}
                  <Card>
                    <Card.Header>
                      <h2 class="text-lg font-semibold text-foreground">Pagos</h2>
                    </Card.Header>
                    <Card.Body>
                      <Show when={data().payments?.length} fallback={<p class="text-sm text-foreground-subtle py-4 text-center">Todavía no se ha intentado ningún cobro.</p>}>
                        <div class="space-y-2">
                          <For each={data().payments ?? []}>
                            {payment => (
                              <div class="rounded-lg border border-border p-3">
                                <div class="flex flex-wrap items-center justify-between gap-2">
                                  <div class="flex items-center gap-2">
                                    <Chip color={paymentStatusColors[payment.status]} variant="soft" size="xs">
                                      {paymentStatusLabels[payment.status]}
                                    </Chip>
                                    <span class="text-sm text-foreground">{payment.provider}</span>
                                    <Show when={payment.mode === 'sandbox'}>
                                      <Chip color="info" variant="soft" size="xs">
                                        Pruebas
                                      </Chip>
                                    </Show>
                                  </div>
                                  <span class="font-medium text-foreground">{money(payment.amount, payment.currency)}</span>
                                </div>

                                <div class="mt-2 text-xs text-foreground-subtle space-y-0.5">
                                  <Show when={payment.method}>
                                    <div>
                                      Medio: {payment.method}
                                      <Show when={payment.installments}> · {payment.installments} cuota(s)</Show>
                                    </div>
                                  </Show>
                                  <Show when={payment.externalId}>
                                    <div>Id en la pasarela: {payment.externalId}</div>
                                  </Show>
                                  <div>Intentado el {formatDateTime(payment.createdAt, settings()?.locale)}</div>
                                  <Show when={payment.failureReason}>
                                    <div class="text-danger">Motivo: {payment.failureReason}</div>
                                  </Show>
                                </div>

                                <Show when={payment.checkoutUrl && payment.status === 'pending'}>
                                  <div class="mt-2">
                                    <Button
                                      size="sm"
                                      variant="text"
                                      color="info"
                                      onClick={() => window.open(payment.checkoutUrl!, '_blank', 'noopener')}
                                      prependIcon="material-symbols-light:open-in-new"
                                    >
                                      Abrir enlace de pago
                                    </Button>
                                  </div>
                                </Show>
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>
                    </Card.Body>
                  </Card>
                </div>

                {/* Columna derecha: totales y comprador */}
                <div class="space-y-4 xl:sticky xl:top-4">
                  <Card>
                    <Card.Header>
                      <h2 class="text-lg font-semibold text-foreground">Resumen</h2>
                    </Card.Header>
                    <Card.Body>
                      <dl class="space-y-2 text-sm">
                        <div class="flex justify-between">
                          <dt class="text-foreground-subtle">Subtotal</dt>
                          <dd>{money(data().subtotal, data().currency)}</dd>
                        </div>
                        <Show when={Number(data().discountAmount) > 0}>
                          <div class="flex justify-between">
                            <dt class="text-foreground-subtle">
                              Descuento
                              <Show when={data().discountType === 'percent'}> ({Number(data().discount)}%)</Show>
                            </dt>
                            <dd>− {money(data().discountAmount, data().currency)}</dd>
                          </div>
                        </Show>
                        <Show when={Number(data().taxAmount) > 0}>
                          <div class="flex justify-between">
                            <dt class="text-foreground-subtle">Impuesto ({Number(data().taxRate)}%)</dt>
                            <dd>{money(data().taxAmount, data().currency)}</dd>
                          </div>
                        </Show>
                        <div class="flex justify-between border-t border-border pt-2 font-semibold text-base">
                          <dt>Total</dt>
                          <dd>{money(data().total, data().currency)}</dd>
                        </div>
                      </dl>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h2 class="text-lg font-semibold text-foreground">Comprador</h2>
                    </Card.Header>
                    <Card.Body>
                      <dl class="space-y-2 text-sm">
                        <div>
                          <dt class="text-foreground-subtle text-xs">Nombre</dt>
                          <dd>{data().customer?.name ?? data().buyer?.name ?? '—'}</dd>
                        </div>
                        <div>
                          <dt class="text-foreground-subtle text-xs">Correo</dt>
                          <dd class="break-all">{data().customer?.email ?? data().buyer?.email ?? '—'}</dd>
                        </div>
                        <div>
                          <dt class="text-foreground-subtle text-xs">Teléfono</dt>
                          <dd>{data().buyer?.phone ?? '—'}</dd>
                        </div>
                      </dl>
                    </Card.Body>
                  </Card>

                  <Show when={data().notes}>
                    <Card>
                      <Card.Header>
                        <h2 class="text-lg font-semibold text-foreground">Notas</h2>
                      </Card.Header>
                      <Card.Body>
                        <p class="text-sm text-foreground-subtle whitespace-pre-line">{data().notes}</p>
                      </Card.Body>
                    </Card>
                  </Show>
                </div>
              </div>
            </div>
          )}
        </Show>
      </Show>

      <alert.AlertComponent />
    </Container>
  )
}

export default PedidoDetallePage
